import React, { useState, useEffect, useRef } from "react";
import { translation } from "../../translation";
import { IoClose } from "react-icons/io5";
import { IoIosImages } from "react-icons/io";
import { FaCamera } from "react-icons/fa";

export default function AutoFillPopup({
  showPopup,
  setShowPopup,
  setOpenIdentification,
  lang,
  upload,
}) {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);

  useEffect(() => {
    if (!showPopup) return;

    const startCamera = async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
        setStream(s);
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.play();
        }
      } catch (err) {
        console.error("Camera error:", err);
      }
    };

    startCamera();

    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
        setStream(null);
      }
    };
  }, [showPopup]);

  const handleTakePicture = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      const type = blob.type || "image/jpeg";
      const extension = type.split("/")[1];
      const file = new File([blob], `picture.${extension}`, { type });
      upload(file);
      handleClose();
    });
  };

  const handleClose = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setShowPopup(false);
    setOpenIdentification(true);
  };

  if (!showPopup) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full relative flex flex-col h-[570px] items-center overflow-hidden">
        {/* Header */}
        <div className="z-30 top-5 left-3 right-3 py-5 px-3 w-full">
          <div className="flex justify-between items-center">
            <button onClick={handleClose} className="text-gray-600 text-xl">
              <IoClose className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-bold text-black text-center flex-1">
              {translation.AutoFillPopup.title[lang] || "Scan or Upload ID"}
            </h2>
            <div className="w-6" />
          </div>

          <p className="text-gray-700 font-light mt-4 text-[14px] text-center">
            {translation.AutoFillPopup.message[lang] ||
              "Align your ID card within the rectangle and take a photo."}
          </p>
        </div>

        {/* Video container with overlay */}
        <div className="relative w-full h-full flex-1 overflow-hidden">
          {/* Camera feed */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Rectangle focus frame */}
          {/* <div
            className="absolute border-4 border-white/90 rounded-lg z-30 pointer-events-none"
            style={{
              top: "50%",
              left: "50%",
              width: "300px",
              height: "190px",
              transform: "translate(-50%, -50%)",
              boxShadow: "0 0 20px rgba(255,255,255,0.5)",
            }}
          /> */}

          <div
            className="absolute top-1/2 left-1/2 z-30 pointer-events-none"
            style={{
              width: "300px",
              height: "190px",
              transform: "translate(-50%, -50%)",
            }}
          >
            {/* Corner lines */}
            <div className="absolute top-0 left-0 w-10 h-1.5 bg-white" />{" "}
            {/* top-left horizontal */}
            <div className="absolute top-0 left-0 w-1.5 h-10 bg-white" />{" "}
            {/* top-left vertical */}
            <div className="absolute top-0 right-0 w-10 h-1.5 bg-white" />{" "}
            {/* top-right horizontal */}
            <div className="absolute top-0 right-0 w-1.5 h-10 bg-white" />{" "}
            {/* top-right vertical */}
            <div className="absolute bottom-0 left-0 w-10 h-1.5 bg-white" />{" "}
            {/* bottom-left horizontal */}
            <div className="absolute bottom-0 left-0 w-1.5 h-10 bg-white" />{" "}
            {/* bottom-left vertical */}
            <div className="absolute bottom-0 right-0 w-10 h-1.5 bg-white" />{" "}
            {/* bottom-right horizontal */}
            <div className="absolute bottom-0 right-0 w-1.5 h-10 bg-white" />{" "}
            {/* bottom-right vertical */}
            {/* Top center line */}
            <div className="absolute top-0 left-1/2 w-12 h-1.5 bg-white -translate-x-1/2" />
            {/* Bottom center line */}
            <div className="absolute bottom-0 left-1/2 w-12 h-1.5 bg-white -translate-x-1/2" />
          </div>

          {/* Top overlay */}
          <div
            className="absolute bg-black/50 z-20"
            style={{
              top: 0,
              left: 0,
              width: "100%",
              height: "calc(50% - 95px)", // half of video - half of rectangle height
            }}
          />

          {/* Bottom overlay */}
          <div
            className="absolute bg-black/50 z-20"
            style={{
              bottom: 0,
              left: 0,
              width: "100%",
              height: "calc(50% - 95px)",
            }}
          />

          {/* Left overlay */}
          <div
            className="absolute bg-black/50 z-20"
            style={{
              top: "calc(50% - 95px)",
              left: 0,
              width: "calc(50% - 150px)", // half of video - half of rectangle width
              height: "190px",
            }}
          />

          {/* Right overlay */}
          <div
            className="absolute bg-black/50 z-20"
            style={{
              top: "calc(50% - 95px)",
              right: 0,
              width: "calc(50% - 150px)",
              height: "190px",
            }}
          />
        </div>

        {/* Bottom buttons */}
        <div className="absolute flex items-center left-5 right-5 justify-between bottom-10 z-40">
          <label className="cursor-pointer">
            <div className="size-10 rounded-full bg-white/40 backdrop-blur-md flex items-center justify-center">
              <IoIosImages size={20} />
            </div>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                if (e.target.files.length > 0) {
                  upload(e.target.files[0]);
                  handleClose();
                }
              }}
            />
          </label>

          <button
            onClick={handleTakePicture}
            className="size-14 border-[4px] border-white rounded-full bg-white/30 backdrop-blur-md flex items-center justify-center"
          >
            <FaCamera size={20} />
          </button>

          <div className="size-10"></div>
        </div>
      </div>
    </div>
  );
}
